import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Link } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  expensablePendingApproversNames,
  isAutomaticExpensable,
  isCardPaymentPending,
  isChangesRequestedExpensable,
  isDraftExpensable,
  isOwnExpensable,
  isPaidExpensable,
  isPendingExpensable,
  isRejectedExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

interface ExpensableAlertsProps {
  expensable: Expensable
  canEditExpensesWithPendingStatus?: boolean
}
const ExpensableAlerts = ({
  expensable,
  canEditExpensesWithPendingStatus,
}: ExpensableAlertsProps) => {
  const { employee } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const showPendingCardPaymentAlert =
    !isDraftExpensable(expensable) &&
    isCardPaymentPending(expensable) &&
    !isOwnExpensable(expensable, employee)
  const showExpenseInDraftAlert =
    isDraftExpensable(expensable) &&
    isAutomaticExpensable(expensable) &&
    !isOwnExpensable(expensable, employee)
  const ownerName = expensable.owner?.fullName ?? i18n.t('expenses_v2.banners.owner_name')
  const { currentUserApprovalStatus } = expensable
  const pendingApproverNames = expensablePendingApproversNames(expensable).join(', ')

  const showPendingApproverAlert =
    !showPendingCardPaymentAlert &&
    isPendingExpensable(expensable) &&
    pendingApproverNames &&
    !isOwnExpensable(expensable, employee)
  const showRejectedAlert = isRejectedExpensable(expensable) && expensable.approvalFlowReason
  const showChangesRequestedAlert =
    isChangesRequestedExpensable(expensable) && expensable.approvalFlowReason
  const showPaidAlert = isPaidExpensable(expensable) && isOwnExpensable(expensable, employee)
  const { formState } = useFormContext()
  const { isValid } = formState

  const showEmptyMandatoryFieldsAlert =
    isPendingExpensable(expensable) &&
    currentUserApprovalStatus === 'pending' &&
    !canEditExpensesWithPendingStatus &&
    !isValid

  const supplement = expensable.supplement
  const policyPeriod = supplement?.policyPeriod
  const compensation = expensable.compensation
  const payrollRun = compensation?.payrollRun
  const periodLabel = payrollRun?.label || policyPeriod?.name
  const showPayrollSupplementAlert = policyPeriod

  const onSeeInPayroll = () => {
    if (!policyPeriod) return

    navigate(
      navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
        policyId: policyPeriod.policyId,
        periodId: policyPeriod.id,
      }),
      { state: { supplementId: supplement.id, employeeId: expensable.owner?.id } }
    )
  }

  const anyAlert =
    showPendingApproverAlert ||
    showRejectedAlert ||
    showChangesRequestedAlert ||
    showPaidAlert ||
    showExpenseInDraftAlert ||
    showPendingCardPaymentAlert ||
    showEmptyMandatoryFieldsAlert ||
    showPayrollSupplementAlert

  if (!anyAlert) return null

  return (
    <Box paddingTop="s16">
      {showPendingApproverAlert && (
        <Alert color="warning">
          {expensable.currentUserApprovalStatus === 'approved'
            ? i18n.t('expenses_v2.expense.status.stepApprovedWithPendingApprover', {
                pendingApproverName: pendingApproverNames,
              })
            : i18n.t('expenses_v2.expense.status.pendingApprover', { name: pendingApproverNames })}
        </Alert>
      )}

      {showRejectedAlert && <Alert color="warning">{expensable.approvalFlowReason}</Alert>}

      {showChangesRequestedAlert && <Alert color="warning">{expensable.approvalFlowReason}</Alert>}

      {showPendingCardPaymentAlert && (
        <Alert color="warning">
          {i18n.t('expenses_v2.banners.automatic_expense_with_pending_payment')}
        </Alert>
      )}

      {showExpenseInDraftAlert && (
        <Alert color="warning">
          {i18n.t('expenses_v2.banners.automatic_expense_still_in_draft', { ownerName })}
        </Alert>
      )}

      {showPaidAlert && (
        <Alert color="warning">
          {i18n.t('expenses_v2.forms.expense_paid_notice', {
            date: dateBackendDateTimeToUI(expensable.paidAt),
          })}
        </Alert>
      )}

      {showEmptyMandatoryFieldsAlert && (
        <Box paddingY="s16">
          <Alert color="warning">{i18n.t('expenses_v2.banners.empty_mandatory_fields')}</Alert>
        </Box>
      )}

      {showPayrollSupplementAlert && (
        <Alert color="info">
          <Box flexDirection="column" gap="s8">
            <>
              {i18n.t('expenses_v2.banners.payroll_supplement', {
                policy_period_name: periodLabel,
              })}
            </>
            <Box alignItems="flexStart">
              <Link.Touchable onClick={onSeeInPayroll}>
                {i18n.t('expenses_v2.list.actions.see_in_payroll')}
              </Link.Touchable>
            </Box>
          </Box>
        </Alert>
      )}
    </Box>
  )
}
export default ExpensableAlerts
